import { Injectable } from '@angular/core';
import { AkitaAuthQuery } from '@app/akita/api/auth/state/auth.query';
import { TranslocoService } from '@ngneat/transloco';
import { formatApplePayPrice } from '../utils/stripe.utils';
import {
  Stripe,
  PaymentRequestOptions,
  PaymentRequest,
  PaymentRequestShippingOption,
  PaymentRequestItem,
} from '@stripe/stripe-js';
import { PriceModel } from '@app/shared/models/api/price.model';
import { Product } from '@app/akita/api/products/models/product.model';
import { Insurance } from '@app/akita/api/products/models/insurance.model';

@Injectable({ providedIn: 'root' })
export class StripePaymentRequestService {
  constructor(
    private readonly akitaAuthQuery: AkitaAuthQuery,
    private readonly translateService: TranslocoService
  ) {}

  public generatePaymentRequestOptions(
    products?: Array<Product> | null,
    insurances?: Array<Insurance> | null,
    pendingPrice?: boolean | null,
    deliveryProvider?: string | null,
    deliveryFee?: number | null,
    popsyFee?: number | null,
    skipPayerEmail?: boolean | null,
    discountAmount?: PriceModel | null,
    protectionPrice?: PriceModel | null
  ): PaymentRequestOptions {
    let requestEmail = !skipPayerEmail;
    if (this.akitaAuthQuery.isLoggedIn) {
      requestEmail = false;
    }

    const country = 'AE'; // Default country, can be changed based on user settings or location

    const calculatedPrices = this.calculatePrices(
      products,
      insurances,
      deliveryProvider,
      deliveryFee,
      popsyFee,
      Boolean(pendingPrice),
      discountAmount,
      protectionPrice
    );

    return {
      country: country,
      requestPayerName: !this.akitaAuthQuery.isLoggedIn,
      requestPayerEmail: requestEmail,
      requestPayerPhone: !this.akitaAuthQuery.isLoggedIn,
      requestShipping: !this.akitaAuthQuery.isLoggedIn,
      currency: `${products?.[0]?.price.currency || ''}`.toLowerCase(),
      ...calculatedPrices,
    };
  }

  private calculatePrices(
    products?: Array<Product> | null,
    insurances?: Array<Insurance> | null,
    deliveryProvider?: string | null,
    delivery?: number | null,
    popsyFee?: number | null,
    pending?: boolean | null,
    discountAmount?: PriceModel | null,
    protectionPrice?: PriceModel | null
  ): {
    total: PaymentRequestItem;
    displayItems: PaymentRequestItem[] | undefined;
    shippingOptions: PaymentRequestShippingOption[] | undefined;
  } {
    const displayItems = new Array(0);
    const shippingOptions = new Array(0);

    let total =
      products?.reduce(
        (tot, current) => tot + current?.price.amount * (current?.quantity || 1),
        0
      ) || 0;

    if (insurances && insurances.length > 0) {
      total = total + insurances.reduce((tot, current) => tot + current.price.amount, 0);
    }

    products?.forEach((x) => {
      displayItems.push({
        amount: formatApplePayPrice(x.price.amount),
        label: x.title || '',
      });
    });

    if (protectionPrice) {
      total = total + protectionPrice.amount;
      displayItems.push({
        amount: formatApplePayPrice(protectionPrice.amount || 0),
        label: this.translateService.translate('txt_popsy_protect'),
      });
    }

    if (discountAmount?.amount) {
      total = total - discountAmount?.amount;

      displayItems.push({
        amount: formatApplePayPrice(discountAmount.amount || 0),
        label: this.translateService.translate('label_discount_code'),
      });
    }

    if (delivery || delivery === 0) {
      total = total + delivery;

      displayItems.push({
        amount: formatApplePayPrice(delivery || 0),
        label: this.translateService.translate('deliver_with_popsy'),
      });

      shippingOptions.push({
        identifier: `${deliveryProvider}`,
        amount: formatApplePayPrice(delivery || 0),
        label: this.translateService.translate('label_delivery_service'),
        detail: this.translateService.translate('deliver_with_popsy'),
      });
    }

    // If Popsy Fee is 0, don't show
    if (popsyFee) {
      total = total + popsyFee;

      displayItems.push({
        amount: formatApplePayPrice(popsyFee || 0),
        label: this.translateService.translate('txt_popsy_protect'),
      });
    }

    return {
      shippingOptions: shippingOptions,
      displayItems: displayItems,
      total: {
        amount: formatApplePayPrice(total),
        label: this.translateService.translate('label_estimated_price_total'),
        pending: Boolean(pending), // Indicate this price is final
      },
    };
  }

  public generatePaymentRequest(
    stripeInstance?: Stripe | null,
    paymentRequestOptions?: PaymentRequestOptions | null
  ): PaymentRequest | null {
    if (stripeInstance && paymentRequestOptions) {
      paymentRequestOptions.country = 'AE'; // Default country, can be changed based on user settings or location

      return stripeInstance.paymentRequest(paymentRequestOptions);
    } else {
      return null;
    }
  }

  /*   public detectIfWalletPaymentSupported(
    paymentRequest?: PaymentRequest | null
  ): Observable<'apple' | 'google' | 'microsoft' | null> {
    return new Observable(
      (observer: Observer<'apple' | 'google' | 'microsoft' | null>) => {
        this.zone.runOutsideAngular(() => {
          if (paymentRequest) {
            paymentRequest
              .canMakePayment()
              .then((isAllowed: { applePay?: boolean } | null) => {
                if (isAllowed) {
                  this.zone.run(() => {
                    if (isAllowed.applePay) {
                      observer.next('apple');
                    } else {
                      if (this.detectBrowserNameService.browserName === 'edge') {
                        observer.next('microsoft');
                      } else {
                        observer.next('google');
                      }
                    }
                    observer.complete();
                  });
                } else {
                  this.zone.run(() => {
                    observer.next(null);
                    observer.complete();
                  });
                }
              })
              .catch(() => {
                this.zone.run(() => {
                  observer.next(null);
                  observer.complete();
                });
              });
          } else {
            this.zone.run(() => {
              observer.next(null);
              observer.complete();
            });
          }
        });
      }
    );
  }

  public onPayWithPaymentRequestButton(
    provider: 'apple' | 'google' | 'microsoft',
    paymentRequest?: PaymentRequest | null,
    item?: PopsyPaymentRequestItem | null,
    currency?: string | null,
    address?: Address | null,
    email?: string | null,
    phone?: {
      countryCode?: string | null;
      areaCode?: string | null;
      national?: string | null;
      display?: string | null;
    } | null,
    fullName?: string | null,
    skipPayerName?: boolean | null,
    skipPayerPhone?: boolean | null,
    skipPayerEmail?: boolean | null,
    skipPayerShipping?: boolean | null,
    callback?: (email?: string | null) => void
  ): Subscription {
    const subscriptions = new Subscription();

    SentryUtil.addBreadcrumb({
      category: `${provider} Pay`,
      message: `User pressed Pay with ${provider} Pay`,
      level: 'info',
      type: 'sentry',
    });

    if (paymentRequest) {
      paymentRequest.on(
        'paymentmethod',
        this.onPaymentMethod(
          subscriptions,
          provider,
          item,
          currency,
          address,
          email,
          phone,
          fullName,
          Boolean(skipPayerName),
          Boolean(skipPayerPhone),
          Boolean(skipPayerEmail),
          Boolean(skipPayerShipping),
          callback
        ).bind(this)
      );

      if (!skipPayerShipping) {
        paymentRequest.on(
          'shippingaddresschange',
          this.onShippingAddressChange(item).bind(this)
        );

        paymentRequest.on('shippingoptionchange', this.onShippingOptionChange.bind(this));
      }

      paymentRequest.on('cancel', this.onPaymentRequestCancel(provider).bind(this));

      paymentRequest.show();
    }

    return subscriptions;
  }

  private onPaymentMethod(
    subscriptions: Subscription,
    provider: 'apple' | 'google' | 'microsoft',
    item?: PopsyPaymentRequestItem | null,
    currency?: string | null,
    address?: Address | null,
    email?: string | null,
    phone?: {
      countryCode?: string | null;
      areaCode?: string | null;
      national?: string | null;
      display?: string | null;
    } | null,
    fullName?: string | null,
    skipPayerName?: boolean | null,
    skipPayerPhone?: boolean | null,
    skipPayerEmail?: boolean | null,
    skipPayerShipping?: boolean | null,
    callback?: (email?: string | null) => void
  ): (response: PaymentRequestPaymentMethodEvent) => void {
    const origEmail = `${email || ''}`;
    return (response: PaymentRequestPaymentMethodEvent): void => {
      subscriptions.add(
        this.verifySessionOrCreateNewOne(
          response,
          item,
          Boolean(skipPayerName),
          Boolean(skipPayerPhone),
          Boolean(skipPayerEmail),
          Boolean(skipPayerShipping)
        )
          .pipe(
            switchMap((isValid: boolean | null) => {
              if (isValid) {
                return this.onAppleOrGooglePayMethod(
                  provider,
                  response,
                  currency,
                  address,
                  email,
                  phone,
                  fullName
                );
              } else {
                return throwError(() =>
                  parseApiError({
                    error: 'fail',
                  })
                );
              }
            }),
            switchMap((validatedPaymentMethodId: string | null) => {
              if (validatedPaymentMethodId) {
                return this.validateOrder(response, item);
              } else {
                return throwError(() =>
                  parseApiError({
                    error: 'fail',
                  })
                );
              }
            })
          )
          .subscribe({
            next: (delivery?: DeliveryInfoModel | null) => {
              if (delivery?.id) {
                SentryUtil.addBreadcrumb({
                  category: `${provider} Pay`,
                  message: `Pay with ${provider} Pay Success!!`,
                  level: 'info',
                  type: 'sentry',
                });

                if (provider === 'apple') {
                  logOnApplePay(
                    this.analytics.logEventWrapper,
                    this.analytics.firebaseRecordPaymentMethodSelectedADConversion
                  );
                } else {
                  logOnGooglePay(
                    this.analytics.logEventWrapper,
                    this.analytics.firebaseRecordPaymentMethodSelectedADConversion
                  );
                }

                response.complete('success');
              } else {
                response.complete('fail');
              }
            },
            error: (error: unknown) => {
              const parsed = parseApiError(error);
              if (parsed.code === 'EMAIL_ALREADY_EXISTS') {
                response.complete('invalid_payer_email');

                if (callback) {
                  callback(origEmail);
                }

                subscriptions.add(
                  this.openAuthDialog(null, origEmail).subscribe({
                    next: () => {},
                    error: () => {},
                  })
                );
              } else if (WALLET_STATUS.indexOf(parsed.name) !== -1) {
                response.complete(parsed.name as PaymentRequestCompleteStatus);
              } else {
                response.complete('fail');
              }
            },
          })
      );
    };
  }

  public onShippingAddressChange(
    item?: PopsyPaymentRequestItem | null
  ): ($event: PaymentRequestShippingAddressEvent) => Subscription {
    return ($event: PaymentRequestShippingAddressEvent) => {
      const subscriptions = new Subscription();
      if (item) {
        const listingId = item.id;
        this.akitaCreateDeliveryFormService.setProductIds([...listingId]);
        this.akitaCreateDeliveryFormService.setTrigger('payment_request_button');

        const countryCode: AllowedDeliveryCountries | null =
          (`${item.country || ''}`.toUpperCase() as AllowedDeliveryCountries) || null;

        if (!countryCode) {
          $event.updateWith({
            status: 'invalid_shipping_address',
            shippingOptions: [],
          });
          return subscriptions;
        }

        if (`${$event.shippingAddress.country || ''}`.toUpperCase() !== countryCode) {
          $event.updateWith({
            status: 'invalid_shipping_address',
            shippingOptions: [],
          });
          return subscriptions;
        }

        this.akitaCreateDeliveryFormService.setCurrency(item.currency);

        // phone?: string;

        const address = new Address();
        address.city = `${$event.shippingAddress.city || ''}`;
        address.country = `${$event.shippingAddress.country || ''}`;

        const addressLines = $event.shippingAddress.addressLine || new Array(0);
        if (addressLines.length > 0) {
          address.address = `${addressLines[0] || ''}`;
          address.streetName = `${addressLines[0] || ''}`;
          address.line1 = `${addressLines[0] || ''}`;
        }
        if (addressLines.length > 1) {
          address.line2 = `${addressLines[1] || ''}`;
        }

        const statePhone = {
          countryCode: this.akitaCreateDeliveryFormQuery.phoneCountryCode,
          areaCode: this.akitaCreateDeliveryFormQuery.phoneAreaCode,
          national: this.akitaCreateDeliveryFormQuery.phoneNational,
          display: this.akitaCreateDeliveryFormQuery.phoneDisplay,
        };
        address.phone = statePhone;

        if ($event.shippingAddress.phone) {
          const parsedPhone = popsyParsePhone($event.shippingAddress.phone);
          if (parsedPhone) {
            address.phone = parsedPhone;
          }
        }

        address.streetNumber = `${$event.shippingAddress.sortingCode || ''}`;
        address.zipCode = `${$event.shippingAddress.postalCode || ''}`;
        address.province = `${$event.shippingAddress.region || ''}`;
        address.neighborhood = `${$event.shippingAddress.dependentLocality || ''}`;
        address.extra = `${
          $event.shippingAddress.recipient ||
          ($event.shippingAddress as any)?.organization ||
          $event.shippingAddress.sortingCode ||
          ''
        }`;
        address.extra = ($event.shippingAddress as any)?.organization || '';
        this.akitaCreateDeliveryFormService.setStoreUserInfo(true);
        this.akitaCreateDeliveryFormService.setDeliveryAddress(address);

        this.akitaCreateDeliveryFormService.setProductIds([...listingId]);
        this.akitaCreateDeliveryFormService.setTrigger('payment_request_button');
        this.akitaCreateDeliveryFormService.setCurrency(item?.currency || null);
        this.akitaCreateDeliveryFormService.setStoreUserInfo(false);
        this.akitaCreateDeliveryFormService.setDeliveryAddress(address);

        subscriptions.add(
          this.akitaRouterService
            .getDiscountPriceModel(this.akitaCreateDeliveryFormQuery.currency)
            .subscribe({
              next: (discount: DiscountInfo | null) => {
                const deliveryProvider = `${
                  this.akitaCreateDeliveryFormQuery.deliveryProvider || ''
                }`;

                const calculatedPrices = this.calculatePrices(
                  item?.title,
                  deliveryProvider,
                  item?.amount,
                  0,
                  0,
                  false,
                  false,
                  discount
                    ? {
                        amount: discount.amount || 0,
                        currency: discount.currency || 'USD',
                      }
                    : null
                );

                $event.updateWith({
                  status: 'success',
                  ...calculatedPrices,
                });
              },
              error: (error: unknown) => {
                console.log(error);
                $event.updateWith({ status: 'fail', shippingOptions: [] });
              },
            })
        );
      } else {
        $event.updateWith({
          status: 'fail',
          shippingOptions: [],
        });
      }
      return subscriptions;
    };
  }

  public onShippingOptionChange($event: PaymentRequestShippingOptionEvent): void {
    $event.updateWith({ status: 'success' });
  }

  private onPaymentRequestCancel(provider: 'apple' | 'google' | 'microsoft'): () => void {
    return (): void => {
      this.zone.runOutsideAngular(() => {
        SentryUtil.addBreadcrumb({
          category: `${provider} Pay`,
          message: `User cancelled Pay with ${provider} Pay`,
          level: 'warning',
          type: 'sentry',
        });

        if (provider === 'apple') {
          logOnApplePayCancelled(this.analytics.logEventWrapper);
        } else {
          logOnGooglePayCancelled(this.analytics.logEventWrapper);
        }
      });
    };
  }

  private verifySessionOrCreateNewOne(
    response: PaymentRequestPaymentMethodEvent,
    item?: PopsyPaymentRequestItem | null,
    skipPayerName?: boolean | null,
    skipPayerPhone?: boolean | null,
    skipPayerEmail?: boolean | null,
    skipPayerShipping?: boolean | null
  ): Observable<boolean> {
    const payerEmail = `${response?.payerEmail || ''}`;
    if (!skipPayerEmail && payerEmail) {
      this.akitaCreateDeliveryFormService.setEmail(payerEmail);
    }

    const payerName = `${response?.payerName || ''}`;
    if (!skipPayerName && payerName) {
      this.akitaCreateDeliveryFormService.setFullName(payerName);
      this.akitaCreateDeliveryFormService.setCardFullName(payerName);
    }

    if (this.akitaAuthQuery.isLoggedIn) {
      return this.isUserInfoValid(
        response,
        item,
        Boolean(skipPayerPhone),
        Boolean(skipPayerShipping)
      );
    } else {
      let email = payerEmail;
      if (skipPayerEmail || !email) {
        email = `${this.akitaCreateDeliveryFormQuery.email || email || ''}`;
      }

      if (email) {
        const username = email.split('@')[0];
        return this.generateAnonymousSession(email, payerName || username).pipe(
          catchError((error: unknown) => {
            const parsed = parseApiError(error);
            if (WALLET_STATUS.indexOf(parsed.code) !== -1) {
              return throwError(() => parsed);
            } else if (parsed.code === 'EMAIL_NOT_VALID') {
              return throwError(() =>
                parseApiError({
                  error: 'invalid_payer_email',
                })
              );
            } else if (parsed.code === 'EMAIL_ALREADY_EXISTS') {
              return throwError(() =>
                parseApiError({
                  error: 'EMAIL_ALREADY_EXISTS',
                })
              );
            } else {
              return throwError(() =>
                parseApiError({
                  error: 'fail',
                })
              );
            }
          }),
          switchMap(() =>
            this.isUserInfoValid(
              response,
              item,
              Boolean(skipPayerPhone),
              Boolean(skipPayerShipping)
            )
          )
        );
      } else {
        return throwError(() =>
          parseApiError({
            error: 'invalid_payer_email',
          })
        );
      }
    }
  }

  private isUserInfoValid(
    response: PaymentRequestPaymentMethodEvent,
    item?: PopsyPaymentRequestItem | null,
    skipPayerPhone?: boolean | null,
    skipPayerShipping?: boolean | null
  ): Observable<boolean> {
    let isValid = false;
    const countryCode: AllowedDeliveryCountries | null =
      (`${item?.country || ''}`.toUpperCase() as AllowedDeliveryCountries) || null;

    if (skipPayerShipping || skipPayerPhone) {
      isValid = true;
    }

    if (
      !skipPayerShipping &&
      (!countryCode || !response?.shippingAddress || !response?.shippingOption?.id)
    ) {
      return throwError(() =>
        parseApiError({
          error: 'invalid_shipping_address',
        })
      );
    } else if (!skipPayerPhone && !response?.payerPhone) {
      return throwError(() =>
        parseApiError({
          error: 'invalid_payer_phone',
        })
      );
    } else if (item) {
      isValid = true;
    } else {
      return throwError(() =>
        parseApiError({
          error: 'fail',
        })
      );
    }

    return of(isValid);
  }

  private validateOrder(
    response: PaymentRequestPaymentMethodEvent,
    item?: PopsyPaymentRequestItem | null
  ): Observable<DeliveryInfoModel | null> {
    if (item) {
      const countryCode = `${
        item?.country || 'US'
      }`.toUpperCase() as AllowedDeliveryCountries;
      this.akitaCreateDeliveryFormService.setCurrency(item.currency);

      const phoneCountryCode = this.akitaCreateDeliveryFormQuery.phoneCountryCode;
      const phoneAreaCode = this.akitaCreateDeliveryFormQuery.phoneAreaCode;
      const phoneNational = this.akitaCreateDeliveryFormQuery.phoneNational;
      const phoneDisplay = this.akitaCreateDeliveryFormQuery.phoneDisplay;
      let parsedPhone: any = {
        countryCode: phoneCountryCode,
        areaCode: phoneAreaCode,
        national: phoneNational,
        display: phoneDisplay,
      };

      if (response.payerPhone) {
        parsedPhone = popsyParsePhone(response.payerPhone || '', countryCode);
      }

      if (response?.payerName) {
        this.akitaCreateDeliveryFormService.setCardFullName(response.payerName);
      }

      if (response?.payerEmail) {
        this.akitaCreateDeliveryFormService.setEmail(response.payerEmail);
      }

      const deliveryAddress = this.akitaCreateDeliveryFormQuery.deliveryAddress;
      const addressDetails = this.akitaCreateDeliveryFormQuery.deliveryAddressDetails;
      const email = this.akitaCreateDeliveryFormQuery.email;
      const documentNumber =
        this.akitaCreateDeliveryFormQuery.taxDocumentNumber ||
        this.akitaCreateDeliveryFormQuery.billingAddressDocumentNumber;
      const fullName =
        this.akitaCreateDeliveryFormQuery.fullName ||
        this.akitaCreateDeliveryFormQuery.billingAddressFullName;

      if (response.shippingAddress) {
        const eventAddress =
          parseAddressJson({
            city: `${response.shippingAddress?.city || deliveryAddress?.city || ''}`,
            country: `${
              response.shippingAddress?.country || deliveryAddress?.country || ''
            }`,
            streetName: `${
              response.shippingAddress?.addressLine?.join(', ') ||
              deliveryAddress?.streetName ||
              ''
            }`,
            zipCode: `${
              response.shippingAddress?.postalCode || deliveryAddress?.zipCode || ''
            }`,
            province: `${
              response.shippingAddress?.region || deliveryAddress?.province || ''
            }`,
            neighborhood: `${deliveryAddress?.neighborhood || ''}`,
            streetNumber: `${deliveryAddress?.streetNumber || ''}`,
            phone: parsePhoneInfo({
              countryCode: parsedPhone?.countryCode || phoneCountryCode || '',
              areaCode: parsedPhone?.areaCode || phoneAreaCode || '',
              national: parsedPhone?.national || phoneNational || '',
              display: parsedPhone?.display || phoneDisplay || '',
            }),
            email: response.payerEmail || email || '',
            fullName: response.payerName || fullName || '',
            documentNumber: documentNumber || '',
            extra:
              (response.shippingAddress as any)?.organization ||
              addressDetails ||
              deliveryAddress?.extra ||
              '',
          }) || new Address();
        eventAddress.address =
          deliveryAddress?.address || generateAddressText(eventAddress);

        if (
          LIST_OF_ALLOWED_DELIVERY_COUNTRY_CODES.indexOf(
            `${eventAddress.country || ''}`.toUpperCase() as AllowedDeliveryCountries
          ) === -1
        ) {
          return throwError(() =>
            parseApiError({
              error: 'invalid_shipping_address',
            })
          );
        }

        return this.tryToGeocodeAddress(eventAddress).pipe(
          switchMap((address: Address) => {
            this.akitaCreateDeliveryFormService.setDeliveryAddress(address);
            this.akitaCreateDeliveryFormService.setBillingAddress(address);
            return this.createOrder(response, item);
          })
        );
      } else {
        return this.createOrder(response, item);
      }
    } else {
      return throwError(() =>
        parseApiError({
          error: 'fail',
        })
      );
    }
  }

  private createOrder(
    response: PaymentRequestPaymentMethodEvent,
    item?: PopsyPaymentRequestItem | null
  ): Observable<DeliveryInfoModel | null> {
    this.akitaCreateDeliveryFormService.setStoreUserInfo(true);
    this.akitaCreateDeliveryFormService.setPaymentMethod('CREDIT_CARD');
    this.akitaCreateDeliveryFormService.setPaymentMethodID(response.paymentMethod.id);
    this.akitaCreateDeliveryFormService.setPaymentBrand(
      (response.paymentMethod.card?.brand || 'unknown') as any
    );
    this.akitaCreateDeliveryFormService.setPaymentLastDigits(
      response.paymentMethod.card?.last4 || ''
    );
    this.akitaCreateDeliveryFormService.moveToStepWithName('step-review');

    return this.paymentDetailsSaved(response, item);

    // TODO: should check here if user signed-in for guest checkout?
    // return this.akitaCreateDeliveryFormService.savePaymentDetails().pipe(
    //   catchError((error: unknown) => {
    //     const parsed = parseApiError(error);
    //     SentryUtil.reportException(parsed, false);
    //     this.akitaCreateDeliveryFormService.moveToStepWithName('step-payment-error');
    //     return throwError(() =>
    //       parseApiError({
    //         error: 'fail',
    //       })
    //     );
    //   }),
    //   switchMap((savePaymentApiResponse?: SavePaymentApiResponse | null) => {
    //     if (savePaymentApiResponse) {
    //       return this.paymentDetailsSaved(response, item);
    //     } else {
    //       this.akitaCreateDeliveryFormService.moveToStepWithName('step-payment-error');
    //       return throwError(() =>
    //         parseApiError({
    //           error: 'fail',
    //         })
    //       );
    //     }
    //   })
    // );
  }

  private paymentDetailsSaved(
    response: PaymentRequestPaymentMethodEvent,
    item?: PopsyPaymentRequestItem | null
  ): Observable<DeliveryInfoModel | null> {
    this.akitaCreateDeliveryFormService.setPaymentProvider('STRIPE');
    this.akitaCreateDeliveryFormService.setPaymentMethod('CREDIT_CARD');
    this.akitaCreateDeliveryFormService.setPaymentMethodID(response.paymentMethod.id);
    this.akitaCreateDeliveryFormService.setPaymentBrand(
      (response.paymentMethod.card?.brand || 'unknown') as any
    );
    this.akitaCreateDeliveryFormService.setPaymentLastDigits(
      response.paymentMethod.card?.last4 || ''
    );
    this.akitaCreateDeliveryFormService.moveToStepWithName('step-review');

    return this.akitaCreateDeliveryFormService.createDelivery().pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        SentryUtil.reportException(parsed, false);
        this.akitaCreateDeliveryFormService.moveToStepWithName('step-payment-error');
        return throwError(() =>
          parseApiError({
            error: 'fail',
          })
        );
      }),
      switchMap(
        (info: DeliveryInfoModel | null): Observable<DeliveryInfoModel | null> => {
          if (info) {
            this.akitaCreateDeliveryFormService.moveToStepWithName('step-success');
            this.onPurchaseComplete(item?.id);

            return of(info);
          } else {
            this.akitaCreateDeliveryFormService.moveToStepWithName('step-payment-error');
            return throwError(() =>
              parseApiError({
                error: 'fail',
              })
            );
          }
        }
      )
    );
  }

  private generateAnonymousSession(
    email: string,
    name: string
  ): Observable<UserModel | null> {
    return this.akitaAuthService.checkIfEmailIsValid(email).pipe(
      catchError((error: unknown) => throwError(() => parseApiError(error))),
      switchMap((status: VerifyUserModel | null) => {
        if (status?.email === VerifyUserStatus.VALID) {
          return this.akitaAuthService.registerUserInBackground(
            email,
            name,
            null,
            this.akitaConfigurationQuery.locale
          );
        } else if (status?.email === VerifyUserStatus.ALREADY_EXISTS) {
          // return this.openAuthDialog(null, email);
          return throwError(() => parseApiError('EMAIL_ALREADY_EXISTS'));
        } else {
          return throwError(() => parseApiError('EMAIL_NOT_VALID'));
        }
      })
    );
  }

  private tryToGeocodeAddress(address: Address): Observable<Address> {
    const newAddress = parseAddressJson(address) || new Address();
    const addressText = generateAddressText(address);
    newAddress.address = addressText;

    const fallbackPosition = getLatLngFromCountryCode(address.country);
    if (fallbackPosition) {
      newAddress.latitude = Number(fallbackPosition.lat);
      newAddress.longitude = Number(fallbackPosition.lng);
    }

    return this.akitaGoogleGeocoderService.queryAddress(addressText).pipe(
      map((results: Array<Coordinate>) => {
        if (results.length > 0) {
          newAddress.latitude = results[0].latitude;
          newAddress.longitude = results[0].longitude;
        }
        return newAddress;
      }),
      catchError((error: unknown) => {
        if ((error as ApiError)?.code !== 'ZERO_RESULTS') {
          console.log(error);
        }
        return of(newAddress);
      })
    );
  }

  private onPurchaseComplete(listingId?: string | null): void {
    if (listingId) {
      const fragment = this.akitaRouterQuery.getDeliveryUrlFragment('new');
      fragment.push(listingId, 'success');
      this.akitaRouterService.navigate(fragment);
    }
  }

  public onAppleOrGooglePayMethod(
    provider: 'apple' | 'google' | 'microsoft',
    response: PaymentRequestPaymentMethodEvent,
    currency?: string | null,
    address?: Address | null,
    email?: string | null,
    phone?: {
      countryCode?: string | null;
      areaCode?: string | null;
      national?: string | null;
      display?: string | null;
    } | null,
    fullName?: string | null
  ): Observable<string | null> {
    const paymentMethodId = `${response?.paymentMethod?.id || ''}`;
    if (paymentMethodId) {
      const setupIntentSecret = this.akitaPaymentQuery.getSetupIntentSecret(currency);
      return this.stripeActionsService
        .confirmCardSetup(
          setupIntentSecret,
          `${currency || ''}`,
          `${paymentMethodId}`,
          address,
          `${email || ''}`,
          `${phone?.display || ''}`,
          `${fullName || ''}`,
          `${address?.zipCode || ''}`
        )
        .pipe(
          catchError((error: unknown) => {
            const parsed = parseApiError(error);
            if (provider === 'apple') {
              logOnApplePayError(this.analytics.logEventWrapper, parsed);
            } else {
              logOnGooglePayError(this.analytics.logEventWrapper, parsed);
            }

            return throwError(() =>
              parseApiError({
                error: 'fail',
              })
            );
          }),
          map((validatedPaymentMethodId?: string | null): string | null => {
            if (validatedPaymentMethodId) {
              return validatedPaymentMethodId;
            } else {
              if (provider === 'apple') {
                logOnApplePayError(this.analytics.logEventWrapper, {
                  name: 'EMPTY_RESPONSE',
                  code: 'EMPTY_RESPONSE',
                  message: 'Empty Response from Stripe',
                });
              } else {
                logOnGooglePayError(this.analytics.logEventWrapper, {
                  name: 'EMPTY_RESPONSE',
                  code: 'EMPTY_RESPONSE',
                  message: 'Empty Response from Stripe',
                });
              }
              return null;
            }
          })
        );
    } else {
      return throwError(() =>
        parseApiError({
          error: 'fail',
        })
      );
    }
  }

  public openAuthDialog(
    mode?: string | null,
    email?: string | null,
    callback?: () => void
  ): Observable<UserModel | null> {
    let observable: Observable<UserModel | null> = of(null);

    if (!this.intShowAuthOverlay) {
      observable = this.authOverlayDynamicService.observe.pipe(
        map((data?: any | null) => {
          this.intShowAuthOverlay = Boolean(data);
          return this.akitaAuthQuery.user;
        })
      );

      logAuthDialogShown(this.analytics.logEventWrapper, 'create-delivery');
      this.authOverlayDynamicService.open({
        data: {
          mode: mode || 'auth-intro',
          email: email || null,
          silent: true,
          handleRedirectCallback: () => {
            this.akitaRouterService.replayOnNavigationEnd();

            if (callback) {
              callback();
            }
          },
        },
        isBrowser: this.akitaRouterQuery.isBrowser,
      });
    }
    return observable;
  } */
}
